import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { isNotEmpty } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';
import piexif from "piexifjs";

import Konva from 'konva';
import { Stage, Layer, Rect, Line, Group, Circle } from 'react-konva';
import { useCanvasMachine } from '../../ts/games/canvasStateMachine.ts';

import ActionButton from './ActionButton.js';
import ActionLink from './ActionLink.js';
import ButtonCollection from './ButtonCollection.js';
import { dataURLtoFile } from './ImageUploader.js';
import Model from './Model.js';
import Range from './Range.js';

import FloatingLayerPublic from '../front/FloatingLayerPublic.js';
import LeveledHTMLText from '../front/helpers/LeveledHTMLText.js';
import TicketCropperInstructions from '../front/stages/TicketCropperInstructions.js';

const ImageCropperCanvas = forwardRef((props, ref) => {
	const image = props?.image ?? {};
	const width = props?.width ?? 800;
	const height = props?.height ?? 800;
	const padding = 20;
	const indent = 0.075;	// in %

	const min_stage_size = Math.min(width, height);

	const min_area = 0.12;	// 12% of the size
	const is_debug = false;

	const mid_point_keys = ['t', 'b', 'l', 'r'];
	const corner_point_keys = ['tl', 'bl', 'tr', 'br'];

	const refs = useRef([]);
	const already_moved = useRef(false);
	const crop = useRef({ t:0, b:0, l:0, r:0, w:1, h:1 });
	const initial_crop = useRef({ t:0, b:0, l:0, r:0, w:1, h:1 });
	const size = useRef({
		w:min_stage_size,
		h:min_stage_size,
		uw:min_stage_size - 2 * padding,	// util width
		uh:min_stage_size - 2 * padding,	// util height
	});

	const img_size = {
		w:(image?.image_w ?? 1000),
		h:(image?.image_h ?? 1500),
	};
	const max_img_size = Math.max(img_size.w, img_size.h);
	const image_scale = size.current.uw / max_img_size;
	const orientation = (img_size.w > img_size.h) ? 'h' : ((img_size.h > img_size.w) ? 'v' : 's');

	const image_coords = {
		t:padding + ((orientation == 'h') ? ((size.current.uh - image_scale * img_size.h) / 2) : 0),
		l:padding + ((orientation == 'v') ? ((size.current.uw - image_scale * img_size.w) / 2) : 0),
	};
	image_coords.b = size.current.uh - image_coords.t + 2 * padding;
	image_coords.r = size.current.uw - image_coords.l + 2 * padding;
	image_coords.w = image_coords.r - image_coords.l;
	image_coords.h = image_coords.b - image_coords.t;

	const image_props = {
		x:size.current.w / 2,
		y:size.current.h / 2,
		width:img_size.w,
		height:img_size.h,
		offsetX:img_size.w / 2,
		offsetY:img_size.h / 2,
		scaleX:image_scale,
		scaleY:image_scale,
		rotation:0,
	};

	const getClipRect = (first_load = false) => {
		const rect_data = {
			x:size.current.uw * crop.current.l + padding,
			y:size.current.uh * crop.current.t + padding,
			w:size.current.uw * crop.current.w,
			h:size.current.uh * crop.current.h,
		};

		if(first_load !== false) {
			initial_crop.current = { ...crop.current };
		}

		return rect_data;
	};

	const updateClipRect = (first_load = false) => {
		const clip_coords = getClipRect(first_load);

		refs.current['image'].setAttrs({
			clipFunc:(ctx) => {
				ctx.rect(
					clip_coords.x,
					clip_coords.y,
					clip_coords.w,
					clip_coords.h,
				);
			},
		});

		const crop_line_props = {
			x:clip_coords.x,
			y:clip_coords.y,
			points:[0, 0, clip_coords.w, 0, clip_coords.w, clip_coords.h, 0, clip_coords.h],
		};

		refs.current['crop_line_shadow'].setAttrs({
			...crop_line_props
		});

		refs.current['crop_line'].setAttrs({
			...crop_line_props
		});

		mid_point_keys.map((p) => {
			let _x = clip_coords.x + (['t', 'b'].includes(p) ? clip_coords.w / 2 : 0);
			if(['r'].includes(p)) _x += clip_coords.w;

			let _y = clip_coords.y + (['l', 'r'].includes(p) ? clip_coords.h / 2 : 0);
			if(['b'].includes(p)) _y += clip_coords.h;

			const transformations = { x:_x, y:_y };

			refs.current[`dragger_${p}`].setAttrs({...transformations});
			refs.current[`visual_dragger_${p}`].setAttrs({...transformations});
		});

		corner_point_keys.map((p) => {
			const _x = clip_coords.x + (['tr', 'br'].includes(p) ? clip_coords.w : 0);
			const _y = clip_coords.y + (['bl', 'br'].includes(p) ? clip_coords.h : 0);

			const transformations = { x:_x, y:_y };

			refs.current[`dragger_${p}`].setAttrs({...transformations});
			refs.current[`visual_dragger_${p}`].setAttrs({...transformations});
		});

		refs.current['mover'].setAttrs({
			x:clip_coords.x,
			y:clip_coords.y,
			width:clip_coords.w,
			height:clip_coords.h,
		});
	};

	const getRelativeCoord = (id, v) => {
		const stage_size = (['t', 'b', 'h'].includes(id)) ? size.current.h : size.current.w;
		const max = stage_size - 2 * padding;
		return (v - padding) / max;
	};

	const getAbsoluteCoord = (id, v) => {
		const stage_size = (['t', 'b', 'h'].includes(id)) ? size.current.h : size.current.w;
		const max = stage_size - 2 * padding;
		return max * v;
	};

	useEffect(() => {
		Object.keys(crop.current).map((coord) => {
			crop.current[coord] = getRelativeCoord(coord, image_coords[coord]);
			crop.current[coord] += indent * ((['t', 'l'].includes(coord)) ? 1 : -1);
		});

		crop.current.w = crop.current.r - crop.current.l;
		crop.current.h = crop.current.b - crop.current.t;

		updateClipRect(true);
	}, []);

	const isMoved = () => {
		if(already_moved.current === true) return;
		if(props.onMove == undefined) return;

		already_moved.current = true;
		props.onMove();
	};

	const moveDraggerH = (id, capture_id) => {
		if(capture_id == undefined) capture_id = id;

		let min_x = Math.max(image_coords.l, padding);
		if(id == 'r') min_x = Math.max(min_x, getAbsoluteCoord('l', crop.current.l + min_area) + padding);

		let max_x = Math.min(image_coords.r, size.current.w - padding);
		if(id == 'l') max_x = Math.min(max_x, getAbsoluteCoord('r', crop.current.r - min_area) + padding);

		const item = refs.current[`dragger_${capture_id}`];
		const final_x = Math.min(max_x, Math.max(min_x, item.attrs.x));
		crop.current[id] = getRelativeCoord(id, final_x);

		crop.current.w = crop.current.r - crop.current.l;
	};

	const moveDraggerV = (id, capture_id) => {
		if(capture_id == undefined) capture_id = id;

		let min_y = Math.max(image_coords.t, padding);
		if(id == 'b') min_y = Math.max(min_y, getAbsoluteCoord('t', crop.current.t + min_area) + padding);

		let max_y = Math.min(image_coords.b, size.current.w - padding);
		if(id == 't') max_y = Math.min(max_y, getAbsoluteCoord('b', crop.current.b - min_area) + padding);

		const item = refs.current[`dragger_${capture_id}`];
		const final_y = Math.min(max_y, Math.max(min_y, item.attrs.y));
		crop.current[id] = getRelativeCoord(id, final_y);

		crop.current.h = crop.current.b - crop.current.t;
	};

	const dragDragger = (id) => {
		if(id.indexOf('t') !== -1) moveDraggerV('t', id);
		if(id.indexOf('b') !== -1) moveDraggerV('b', id);
		if(id.indexOf('l') !== -1) moveDraggerH('l', id);
		if(id.indexOf('r') !== -1) moveDraggerH('r', id);

		isMoved();
		updateClipRect();
	};

	const dragWhole = () => {
		const new_x = refs.current['mover'].attrs.x;
		const new_y = refs.current['mover'].attrs.y;

		crop.current.t = getRelativeCoord('t', Math.max(image_coords.t, Math.min(image_coords.b - getAbsoluteCoord('h', crop.current.h), new_y)));
		crop.current.l = getRelativeCoord('l', Math.max(image_coords.l, Math.min(image_coords.r - getAbsoluteCoord('w', crop.current.w), new_x)));
		crop.current.b = crop.current.t + crop.current.h;
		crop.current.r = crop.current.l + crop.current.w;

		isMoved();
		updateClipRect();
	};

	useImperativeHandle(ref, () => ({
		apply_transformation(tr) {
			refs.current['image_src'].setAttrs({ ...tr });
		},

		to_image(format = 'base64') {
			const small_side_size = 1000;

			const o_large_side = Math.max(image.image_w, image.image_h);

			let r_width = crop.current.w * o_large_side;
			let r_height = crop.current.h * o_large_side;

			const r_orientiation = (r_width > r_height) ? 'h' : 'v';
			const scale = Math.min(1, small_side_size / ((r_orientiation == 'h') ? r_height : r_width));

			const crop_stage = new Konva.Stage({
				container:'export_img',
				width:r_width * scale,
				height:r_height * scale,
			});

			const final_crop = { ...crop.current };

			Object.keys(final_crop).map((coord) => {
				final_crop[coord] += indent * ((['t', 'l'].includes(coord)) ? 1 : -1);
			});

			const crop_img = new Konva.Rect({
				width:image.image_w * scale,
				height:image.image_h * scale,
				fillPatternImage:image.fillPatternImage,
				fillPatternScaleX:scale,
				fillPatternScaleY:scale,
				x:-1 * (final_crop.l - initial_crop.current.l) * o_large_side * scale,
				y:-1 * (final_crop.t - initial_crop.current.t) * o_large_side * scale,
			});

			const crop_group = new Konva.Group({
				width:image.image_w,
				height:image.image_h,
				offsetX:image.image_w / 2,
				offsetY:image.image_h / 2,
				x:image.image_w / 2,
				y:image.image_h / 2,
				rotation:refs.current['image_src'].attrs.rotation,
			});

			const crop_layer = new Konva.Layer();
			crop_group.add(crop_img);
			crop_layer.add(crop_group);
			crop_stage.add(crop_layer);

			const dataURL = crop_stage.toDataURL({
				mimeType:'image/jpeg',
				quality:0.95,
				pixelRatio:1,
			});

			if(format == 'base64') {
				if(props.exif) {
					try {
						const exifBytes = piexif.dump(props.exif);
						// insert EXIF ​​bytes into base64 image
						return piexif.insert(exifBytes, dataURL);
					} catch(error) {
						return dataURL;
					}
				}

				return dataURL;
			}

			return dataURLtoFile(dataURL, 'filename.jpg');
		},
	}), []);

	const main_classes = ['canvas_cont'];

	const dragger_radius = 30;
	const visual_dragger_radius = 10;

	const controls_color = `rgba(${props?.items_color ?? '255, 255, 255'}, 1)`;
	const controls_stroke_color = `rgba(${props?.bg_color ?? '0, 0, 0'}, 1)`;

	return (
		<>
			<Stage
				ref={(node) => { refs.current['stage'] = node; }}
				class={main_classes.join(" ")}
				width={size.current.w}
				height={size.current.h}
			>
				<Layer
					ref={(node) => { refs.current['layer'] = node; }}
				>
					<Rect
						{...image_props}
						fillPatternImage={image.fillPatternImage}
					/>

					<Rect
						{...image_props}
						fill='black'
						opacity={0.6}
					/>

					<Group
						ref={(node) => { refs.current['image'] = node; }}
						clipFunc={(ctx) => {
							ctx.rect(
								size.current.uw * crop.current.l + padding,
								size.current.uh * crop.current.t + padding,
								size.current.uw * (crop.current.r - crop.current.l),
								size.current.uh * (crop.current.b - crop.current.t),
							);
						}}
					>
						<Rect
							{...image_props}
							ref={(node) => { refs.current['image_src'] = node; }}
							fillPatternImage={image.fillPatternImage}
						/>
					</Group>

					<Line
						ref={(node) => { refs.current['crop_line_shadow'] = node; }}
						stroke={controls_stroke_color}
						strokeWidth={2 + 3}
						x={size.current.uw * crop.current.l + padding}
						y={size.current.uh * crop.current.t + padding}
						points={[
							0, 0,
							size.current.uw * (crop.current.r - crop.current.l), 0,
							size.current.uw * (crop.current.r - crop.current.l), size.current.uw * (crop.current.b - crop.current.t),
							0, size.current.uw * (crop.current.b - crop.current.t),
						]}
						closed={true}
					/>

					<Line
						ref={(node) => { refs.current['crop_line'] = node; }}
						stroke={controls_color}
						strokeWidth={2}
						x={size.current.uw * crop.current.l + padding}
						y={size.current.uh * crop.current.t + padding}
						points={[
							0, 0,
							size.current.uw * (crop.current.r - crop.current.l), 0,
							size.current.uw * (crop.current.r - crop.current.l), size.current.uw * (crop.current.b - crop.current.t),
							0, size.current.uw * (crop.current.b - crop.current.t),
						]}
						closed={true}
					/>

					{[ ...mid_point_keys, ...corner_point_keys ].map((point) => {
						return (
							<Circle
								key={`visual_dragger_${point}`}
								ref={(node) => { refs.current[`visual_dragger_${point}`] = node; }}
								radius={visual_dragger_radius}
								x={size.current.w / 2}
								fill={controls_color}
								strokeWidth={2}
								stroke={controls_stroke_color}
							/>
						)
					})}

					<Rect
						ref={(node) => { refs.current['mover'] = node; }}
						onDragMove={() => dragWhole()}
						draggable={true}
						width={image_scale * img_size.w}
						height={image_scale * img_size.h}
						fill='yellow'
						opacity={(is_debug) ? 0.2 : 0}
					/>

					{[ ...mid_point_keys, ...corner_point_keys ].map((point) => {
						return (
							<Circle
								key={`dragger_${point}`}
								ref={(node) => { refs.current[`dragger_${point}`] = node; }}
								radius={dragger_radius}
								x={size.current.w / 2}
								fill='purple'
								opacity={(is_debug) ? 0.5 : 0}
								draggable={true}
								onDragMove={() => dragDragger(point)}
							/>
						)
					})}
				</Layer>
			</Stage>

			<div
				id="export_img"
				style={{ display:'none' }}
			/>
		</>
	)
});

const ImageCropper = (props) => {
	useCSS('comp/image_cropper.css');

	const crop_mandatory_msg = (isNotEmpty(props, 'crop_mandatory_msg')) ? props.crop_mandatory_msg : false;
	const crop_mandatory = (crop_mandatory_msg !== false) ? true : false;

	const [ is_loaded, setIsLoaded ] = useState(false);
	const [ model, setModel ] = useState({ rotation:0 });
	const [ cta_disabled, setCTADisabled ] = useState((crop_mandatory) ? true : false);
	const [ display_crop_advise, setDisplayCropAdvise ] = useState(true);
	const [ animation_width, setAnimationWidth ] = useState(false);

	const refs = useRef({});

	const width = (props?.width ?? 800) - 30;
	const height = (props?.height ?? 800) - 30;
	const enable_rotation = false;

	const export_image = false;

	const exportToBase64 = () => {
		const image_b64 = refs.current['cropper'].to_image('base64');
		if(export_image === false) {
			if(props.onCrop != undefined) props.onCrop(image_b64);
			return;
		}

		const a = document.createElement('a');
		a.href = image_b64;
		a.download = "img.jpg";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const canvas_images = () => {
		const images = [];
		if(isNotEmpty(props, 'image')) {
			images.push({
				id:'image',
				name:'file',
				url:props.image,
				fit:'contain',
			});
		}

		setIsLoaded(true);
		
		return images;
	};

	const change = (k, v) => {
		const _model = { ...model, [k]:v };
		setModel(_model);
		refs.current['cropper'].apply_transformation(_model);
	};

	const onMove = () => {
		setCTADisabled(false);
	};

	useState(() => {
		setTimeout(() => {
			const item = document.querySelector('.animation');
			const width = item.clientWidth;
			setAnimationWidth(Math.floor(width));
		}, 300);
	}, []);

	const [ state, dispatch ] = useCanvasMachine({
		images:canvas_images,
		images_dependencies:[ props.image ],
	});

	if(is_loaded === false) return null;
	if(state.images.length < 1) return null;

	const items_color = '255, 255, 255';
	const bg_color = '0, 0, 0';
	const exif = props?.exif ?? null;

	return (
		<div
			class="gui_image_cropper is_fullscreen"
			style={{
				'--_items-color':items_color,
				'--_bg-color':bg_color,
			}}
		>
			<ImageCropperCanvas
				ref={(node) => { refs.current['cropper'] = node; }}
				exif={exif}
				image={state.images?.image}
				width={width}
				height={height}
				items_color={items_color}
				bg_color={bg_color}
				onMove={onMove}
			/>

			<div class={(enable_rotation) ? 'with_transformations' : null}>
				{(enable_rotation) && (
					<Model
						model={model}
						change={change}
					>
						<Range
							min={-15}
							max={15}
							value={parseFloat(model?.rotation ?? 0)}
							name="rotation"
							default={0}
							step={0.1}
							//_class={{ size:60 }}
						>Rotation</Range>
					</Model>
				)}

				<ButtonCollection
					_class={{ other:'flex_button gui_big_button' }}
				>
					<ActionButton
						onClick={(cta_disabled) ? () => setDisplayCropAdvise(true) : exportToBase64}
						_class={{ other:(cta_disabled) ? 'semi_disabled' : '' }}
					>{props?.cta_label ?? "Crop"}</ActionButton>
				</ButtonCollection>

				{(props.onCancel != undefined) && (
					<ActionLink
						onClick={props.onCancel}
						_class={{ other:'close_link gui_not_in_top_small' }}
					>Cancel</ActionLink>
				)}
			</div>

			{(isNotEmpty(props, 'footer_message')) && (
				<p
					class="gui_not_in_top_small"
					style={{ '--text-color':`${items_color}`, padding:'0 0.5em' }}
					dangerouslySetInnerHTML={{
						__html:props.footer_message,
					}}
				/>
			)}

			{/*(props.onCancel != undefined) && (
				<SquaredButton
					icon='knk-off close_btn'
					onClick={props.onCancel}
				/>
			)*/}

			{(display_crop_advise) && (
				<FloatingLayerPublic
					pm={props.promotion}
					backButton={() => setDisplayCropAdvise(false)}
				>
					<LeveledHTMLText>
						{crop_mandatory_msg}
					</LeveledHTMLText>

					<div class="animation gui_not_in_top_small" style={{ width:'100%', display:'flex', justifyContent:'space-between' }}>
						{(animation_width !== false) && (
							<>
								<TicketCropperInstructions
									width={animation_width}
								/>
							</>
						)}
					</div>

					<ButtonCollection
						_class={{ other:'flex_button gui_not_in_top' }}
					>
						<ActionButton
							_fullclass=""
							onClick={() => setDisplayCropAdvise(false)}
						>OK, got it!</ActionButton>
					</ButtonCollection>
				</FloatingLayerPublic>
			)}
		</div>
	)
};

export default ImageCropper;